/**
 * Appointments React Query Hooks
 * 
 * This module provides React Query hooks for the Appointments module:
 * - useAppointments: List with filters, pagination, sorting
 * - useAppointment: Single appointment detail
 * - useAppointmentQuotes: Quotes for an appointment
 * - useAppointmentFilters: Filter options
 * - usePendingSummaries: Pending summaries list
 * - useAppointmentMutations: Update/Delete operations with cache invalidation
 * 
 * All queries include tenant_id in the query key to prevent cross-tenant cache bleed.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { useTenant } from "@/contexts/TenantContext";

/**
 * Serialize filter object to a stable query param format
 */
const serializeFilters = (obj) => {
  const clauses = [];
  Object.entries(obj || {}).forEach(([field, cfg]) => {
    if (!cfg?.enabled) return;
    const operator = cfg.operator;
    if (!operator) return;
    
    const dateNoValueOps = new Set([
      "this_week", "this_month", "this_year",
      "last_week", "last_month", "last_year",
      "last_30_days", "last_60_days", "last_90_days", "ytd",
    ]);

    if (["is_empty", "is_not_empty"].includes(operator) || dateNoValueOps.has(operator)) {
      clauses.push({ field, operator });
      return;
    }
    if (cfg.values && Array.isArray(cfg.values) && cfg.values.length > 0) {
      clauses.push({ field, operator, values: cfg.values });
      return;
    }
    const v = String(cfg.value || "").trim();
    if (!v) return;

    if (operator === "between") {
      const vTo = String(cfg.value_to || "").trim();
      if (vTo) clauses.push({ field, operator, value: v, value_to: vTo });
      return;
    }

    clauses.push({ field, operator, value: v });
  });
  return clauses;
};

/**
 * Hook: useAppointmentsList
 * Fetches paginated, filtered, sorted appointments list
 */
export function useAppointmentsList({
  filters = {},
  search = "",
  page = 0,
  pageSize = 10,
  sortBy = null,
  sortDir = "desc",
  enabled = true,
}) {
  const { selectedTenantId } = useTenant();
  
  // Create a stable key from filters
  const filtersKey = JSON.stringify(serializeFilters(filters));
  
  return useQuery({
    queryKey: queryKeys.appointments.list(
      selectedTenantId,
      filtersKey,
      { offset: page * pageSize, limit: pageSize },
      { sortBy, sortDir }
    ),
    queryFn: async () => {
      const res = await api.get("/appointments", {
        params: {
          q: search || undefined,
          filters: filtersKey,
          sort_by: sortBy || undefined,
          sort_dir: sortBy ? sortDir : undefined,
          limit: pageSize,
          offset: page * pageSize,
          group_by_lead: true,
        },
      });
      return {
        items: res.data.items || [],
        total: res.data.total || 0,
      };
    },
    enabled,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook: useCalendarAppointments
 * Fetches appointments for calendar view (wider date range, no pagination)
 */
export function useCalendarAppointments({ enabled = true }) {
  const { selectedTenantId } = useTenant();
  
  return useQuery({
    queryKey: [...queryKeys.appointments.all(selectedTenantId), "calendar"],
    queryFn: async () => {
      const res = await api.get("/appointments", {
        params: {
          limit: 200,
          offset: 0,
          sort_by: "start_at",
          sort_dir: "asc",
        },
      });
      return (res.data.items || []).filter((item) => item.start_at);
    },
    enabled,
    staleTime: 60 * 1000, // 1 minute for calendar data
  });
}

/**
 * Hook: usePendingSummaries
 * Fetches appointments pending summary completion
 */
export function usePendingSummaries({ limit = 5, offset = 0 }) {
  const { selectedTenantId } = useTenant();
  
  return useQuery({
    queryKey: [...queryKeys.appointments.pendingSummaries(selectedTenantId), { limit, offset }],
    queryFn: async () => {
      const res = await api.get("/appointments/pending-summaries", {
        params: { limit, offset },
      });
      return {
        items: res.data.items || [],
        total: res.data.total || 0,
      };
    },
    staleTime: 30 * 1000,
  });
}

/**
 * Hook: useAppointmentFilters
 * Fetches filter field definitions and options
 */
export function useAppointmentFilters() {
  const { selectedTenantId } = useTenant();
  
  return useQuery({
    queryKey: queryKeys.appointments.filters(selectedTenantId),
    queryFn: async () => {
      const res = await api.get("/appointments/filters");
      return {
        fields: Array.isArray(res.data?.fields) ? res.data.fields : [],
        optionsByField: res.data?.options_by_field || {},
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - filter options don't change often
  });
}

/**
 * Hook: useAppointmentDetail
 * Fetches a single appointment by ID
 */
export function useAppointmentDetail(appointmentId, { enabled = true } = {}) {
  const { selectedTenantId } = useTenant();
  
  return useQuery({
    queryKey: queryKeys.appointments.detail(selectedTenantId, appointmentId),
    queryFn: async () => {
      const res = await api.get(`/appointments/${appointmentId}`);
      return res.data;
    },
    enabled: enabled && Boolean(appointmentId),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook: useAppointmentQuotes
 * Fetches quotes for an appointment
 */
export function useAppointmentQuotes(appointmentId, { enabled = true } = {}) {
  const { selectedTenantId } = useTenant();
  
  return useQuery({
    queryKey: queryKeys.appointments.quotes(selectedTenantId, appointmentId),
    queryFn: async () => {
      const res = await api.get(`/appointments/${appointmentId}/quotes`);
      return res.data || { prospects: [], runs: [] };
    },
    enabled: enabled && Boolean(appointmentId),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook: useAppointmentDiscovery
 * Fetches discovery data for an appointment
 */
export function useAppointmentDiscovery(appointmentId, { runId = null, enabled = true } = {}) {
  const { selectedTenantId } = useTenant();
  
  return useQuery({
    queryKey: [...queryKeys.appointments.discovery(selectedTenantId, appointmentId), runId],
    queryFn: async () => {
      const res = await api.get(`/appointments/${appointmentId}/discovery`, {
        params: runId ? { run_id: runId } : {},
      });
      return res.data || {
        run_id: null,
        run_at: null,
        script_name: null,
        prospects: [],
        fields: [],
        values: [],
        runs: [],
      };
    },
    enabled: enabled && Boolean(appointmentId),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook: useAppointmentMutations
 * Provides mutation functions for appointments with cache invalidation
 */
export function useAppointmentMutations() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  
  // Update appointment
  const updateAppointment = useMutation({
    mutationFn: async ({ appointmentId, data }) => {
      const res = await api.put(`/appointments/${appointmentId}`, data);
      return res.data;
    },
    onSuccess: (data, { appointmentId }) => {
      // Invalidate the specific appointment detail
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.detail(selectedTenantId, appointmentId),
      });
      // Invalidate the appointments list to reflect any status changes
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.all(selectedTenantId),
      });
      // Also invalidate pending summaries
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.pendingSummaries(selectedTenantId),
      });
    },
  });
  
  // Delete appointment
  const deleteAppointment = useMutation({
    mutationFn: async (appointmentId) => {
      await api.delete(`/appointments/${appointmentId}`);
      return appointmentId;
    },
    onSuccess: (appointmentId) => {
      // Remove the appointment from cache
      queryClient.removeQueries({
        queryKey: queryKeys.appointments.detail(selectedTenantId, appointmentId),
      });
      // Invalidate the list
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.all(selectedTenantId),
      });
      // Invalidate pending summaries
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.pendingSummaries(selectedTenantId),
      });
    },
  });
  
  // Submit summary
  const submitSummary = useMutation({
    mutationFn: async ({ appointmentId, summaryData }) => {
      const res = await api.post(`/appointments/${appointmentId}/summary`, summaryData);
      return res.data;
    },
    onSuccess: (data, { appointmentId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.detail(selectedTenantId, appointmentId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.all(selectedTenantId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.pendingSummaries(selectedTenantId),
      });
    },
  });
  
  // Mark as sold
  const markAsSold = useMutation({
    mutationFn: async ({ appointmentId, soldData }) => {
      const res = await api.post(`/appointments/${appointmentId}/sold`, soldData);
      return res.data;
    },
    onSuccess: (data, { appointmentId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.detail(selectedTenantId, appointmentId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.appointments.all(selectedTenantId),
      });
      // Also invalidate clients since a sale creates/updates a client
      queryClient.invalidateQueries({
        queryKey: queryKeys.clients.all(selectedTenantId),
      });
    },
  });
  
  return {
    updateAppointment,
    deleteAppointment,
    submitSummary,
    markAsSold,
  };
}

/**
 * Hook: useInvalidateAppointments
 * Returns a function to invalidate all appointments-related queries
 * Useful for manual refresh scenarios
 */
export function useInvalidateAppointments() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  
  return () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.appointments.all(selectedTenantId),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.appointments.pendingSummaries(selectedTenantId),
    });
  };
}

export default {
  useAppointmentsList,
  useCalendarAppointments,
  usePendingSummaries,
  useAppointmentFilters,
  useAppointmentDetail,
  useAppointmentQuotes,
  useAppointmentDiscovery,
  useAppointmentMutations,
  useInvalidateAppointments,
};
